import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Dialog, Input, Radio, Select } from 'tdesign-react';
import { createPermissionBase, createPermissionCustom } from '@/services/kafkaPermissionsService';
import { toTsOptions } from '@/utils/common';
import Form from '@/components/Form';
import {
  fetchAclOperationOptions,
  fetchGroupOptions,
  fetchResourceOptions,
  fetchResourceTypeOptions,
  fetchRoleOptions,
} from '@/slices/kafkaPermissionsSlice';
import useDisableButton from '@/components/DisableWrapper/useDisableButton';

const options = [
  { label: '配置生产权限', value: 'produce' },
  { label: '配置消费权限', value: 'consume' },
  { label: '配置自定义权限', value: 'custom' },
];

const { FormItem } = Form;

function Modal({
  handleClose,
  fetchResourceOptions,
  roleOptions,
  resourceOptions,
  clusterName,
  fetchRoleOptions,
  fetchGroupOptions,
  visible,
  groupOptions,
  aclOperationOptions,
  resourceTypeOptions,
  fetchResourceTypeOptions,
  fetchAclOperationOptions,
  ...others
}) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const aclType = Form.useWatch('aclType', form);
  const groupNameType = Form.useWatch('groupNameType', form);
  const confirmBtn = useDisableButton({ loading: submitting, content: '确定' });

  useEffect(() => {
    if (visible) {
      fetchResourceOptions({ clusterName });
      fetchRoleOptions({ clusterName });
      fetchGroupOptions({ clusterName });
      fetchResourceTypeOptions(clusterName);
    } else {
      form.resetFields();
    }
  }, [
    form,
    visible,
    clusterName,
    fetchResourceOptions,
    fetchRoleOptions,
    fetchGroupOptions,
    fetchResourceTypeOptions,
    fetchAclOperationOptions,
  ]);

  const handleResourceType = (resourceType) => {
    fetchAclOperationOptions({ clusterName, resourceType });
    form.resetFields(['aclOperation']);
  };

  const handleSubmit = async (fields) => {
    const { aclType } = fields;
    const params = { ...fields, clusterName };
    let createRequest = createPermissionBase;
    if (aclType === 'custom') {
      createRequest = createPermissionCustom;
    }
    switch (aclType) {
      // 消费用参数
      case 'produce':
        break;

      case 'consume':
        if (params.groupNameType === 'All') {
          params.groupName = '*';
        }
        delete params.groupNameType;
        break;

      case 'custom':
        params.principal = params.kafkaUser;
        delete params.aclType;
        delete params.kafkaUser;
        break;

      default:
        break;
    }
    try {
      setSubmitting(true);
      await createRequest(params);
      handleClose(true);
    } catch (error) {
      console.trace(error);
      throw error;
    } finally {
      setSubmitting(false);
    }
  };

  const doClose = () => handleClose();

  return (
    <Dialog
      header={'新增ACL'}
      onCancel={doClose}
      onConfirm={() => form.submit()}
      confirmBtn={confirmBtn}
      onCloseBtnClick={doClose}
      onEscKeydown={doClose}
      visible={visible}
      width={550}
      {...others}
    >
      <Form
        onFinish={handleSubmit}
        form={form}
        style={{ width: '90%', paddingBottom: 10 }}
        labelWidth={160}
        initialValues={{
          aclType: 'produce',
          topicPatternType: 'Literal',
          groupPatternType: 'Literal',
          patternType: 'Literal',
          host: '*',
          aclPermissionType: 'Allow',
        }}
      >
        <FormItem name='aclType' label='ACL用途' rules={[{ required: true }]}>
          <Select options={toTsOptions(options)} placeholder='请选择' />
        </FormItem>
        <FormItem name='kafkaUser' label='Kafka User' rules={[{ required: true }]}>
          <Select options={toTsOptions(roleOptions)} placeholder='kafkaUser' clearable />
        </FormItem>
        {['produce', 'consume'].includes(aclType) && (
          <>
            <FormItem
              name='topicPatternType'
              label='Topic Pattern Type'
              rules={[{ required: true }]}
            >
              <Radio.Group>
                <Radio value='Literal'>Literal</Radio>
                <Radio value='Prefixed'>Prefixed</Radio>
              </Radio.Group>
            </FormItem>
            <FormItem name='topicName' label='Topic' rules={[{ required: true }]}>
              <Select
                options={toTsOptions(resourceOptions)}
                placeholder='Topic'
                clearable
                filterable
              />
            </FormItem>
          </>
        )}
        {aclType === 'consume' && (
          <>
            <FormItem
              name='groupPatternType'
              label='Group Pattern Type'
              rules={[{ required: true }]}
            >
              <Radio.Group>
                <Radio value='Literal'>Literal</Radio>
                <Radio value='Prefixed'>Prefixed</Radio>
              </Radio.Group>
            </FormItem>
            <FormItem name='groupNameType' label='Group' rules={[{ required: true }]}>
              <Radio.Group>
                <Radio value='All'>All</Radio>
                <Radio value='Custom'>Custom</Radio>
              </Radio.Group>
            </FormItem>
            {groupNameType === 'Custom' && (
              <FormItem
                name='groupName'
                label=' '
                requiredMark={false}
                rules={[{ required: true }]}
              >
                <Select
                  options={toTsOptions(groupOptions)}
                  placeholder='Group'
                  clearable
                  filterable
                />
              </FormItem>
            )}
          </>
        )}

        {aclType === 'custom' && (
          <>
            <FormItem name='patternType' label='Pattern Type' rules={[{ required: true }]}>
              <Radio.Group>
                <Radio value='Literal'>Literal</Radio>
                <Radio value='Prefixed'>Prefixed</Radio>
              </Radio.Group>
            </FormItem>
            <FormItem name='aclPermissionType' label='Permission Type' rules={[{ required: true }]}>
              <Radio.Group>
                <Radio value='Allow'>Allow</Radio>
                <Radio value='Deny'>Deny</Radio>
              </Radio.Group>
            </FormItem>
            <FormItem name='resourceType' label='Resource Type' rules={[{ required: true }]}>
              <Select options={resourceTypeOptions} onChange={handleResourceType} />
            </FormItem>
            <FormItem name='aclOperation' label='Operation' rules={[{ required: true }]}>
              <Select options={aclOperationOptions} placeholder='Operation' clearable />
            </FormItem>
            <FormItem name='host' label='Host' rules={[{ required: true }]}>
              <Input placeholder='Host' />
            </FormItem>
          </>
        )}
      </Form>
    </Dialog>
  );
}

export default connect(
  (state) => ({
    roleOptions: state.kafkaPermissions.roleOptions,
    resourceOptions: state.kafkaPermissions.resourceOptions,
    groupOptions: state.kafkaPermissions.groupOptions,
    aclOperationOptions: state.kafkaPermissions.aclOperationOptions,
    resourceTypeOptions: state.kafkaPermissions.resourceTypeOptions,
  }),
  {
    fetchResourceOptions,
    fetchRoleOptions,
    fetchGroupOptions,
    fetchResourceTypeOptions,
    fetchAclOperationOptions,
  },
)(Modal);
